import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { createApiClient } from '../api/client';
import { curatedErrorMessage } from '../lib/curatedError';
import { useAccessToken } from '../auth/useAuth';
import { useWorkspace } from './WorkspaceProvider';
import type { CreateWorkspaceResponse, WorkspaceQuota } from '../api/types';
import { useFocusTrap } from '../lib/useFocusTrap';
import { hasVisibleContent } from '@steward-app/steward-types';
import { INVISIBLE_NAME_MESSAGE } from '../lib/nameCopy';
import { slugify, clampSlug, slugTooShort, SLUG_MAX } from '../lib/workspaceSlug';
import { SlugShortNote } from './SlugShortNote';
import { workspaceKey } from '../lib/workspaceQueryKey';
import { cls as formCls } from '../components/formStyles';

const ICON_STROKE = 1.75;

const NEW_WORKSPACE_FAILED_MESSAGE = 'Could not add the workspace.';
/** No figure and no tier name: the SPA has the plan's limit only from `GET /workspaces/quota`, and
 *  the in-dialog at-cap notice (which does show it) is the place for that. */
export const NEW_WORKSPACE_ERROR_COPY: Readonly<Record<string, string>> = {
  QUOTA_EXCEEDED: 'You’ve reached your plan’s limit on workspaces. To add another, upgrade your plan.',
  // Bare status key on purpose, no `TRANSACTION_CANCELLED` twin: a 409 that is not QUOTA_EXCEEDED
  // is a slug collision whatever code it carries (the old code said the same for any 409).
  '409': 'That workspace name or slug is already taken.',
};

/**
 * "Add a workspace" (O3 §6 / WC2). Adds another workspace to the caller's current
 * org via the façade (`POST /workspaces` → appcore, tenant-admin enforced), then
 * refreshes /me and switches into it. Only org-admins reach this (the affordance is
 * gated in the switcher / picker — decisions log §41); a user with no org can't create
 * one here (that's the founder bootstrap flow). Shows the plan's remaining workspace
 * quota and, at the cap, an upsell instead of the form (appcore also 409s as a backstop).
 */
export function NewWorkspaceDialog({ onClose }: { onClose: () => void }) {
  const token = useAccessToken();
  const { activeMembership, activeWorkspaceId, memberships, refresh, switchWorkspace } = useWorkspace();
  const tenantId = activeMembership?.tenantId ?? memberships[0]?.tenantId ?? null;

  // Remaining-quota display (WC2). Keyed by the active workspace (the façade resolves
  // the owning tenant from it server-side). Fail-open: if this errors, the form still
  // submits and appcore's 409 QUOTA_EXCEEDED remains the authoritative guard.
  const { data: quota } = useQuery<WorkspaceQuota>({
    queryKey: workspaceKey('workspace-quota', activeWorkspaceId),
    queryFn: () => createApiClient(token!).get<WorkspaceQuota>(`/workspaces/quota?workspaceId=${encodeURIComponent(activeWorkspaceId!)}`),
    enabled: !!token && !!activeWorkspaceId,
    staleTime: 60_000,
  });
  // Only a positively-known zero blocks the form; null limit (unlimited) or an unresolved
  // query never blocks.
  const atCap = quota?.remaining === 0;

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const effectiveSlug = slugTouched ? clampSlug(slug) : slugify(name);
  // A name of only invisible characters is held back like an empty one, with the reason shown
  // in the slot this form uses for its other errors (crossmarc/steward-app#2041).
  const invisibleName = name.trim().length > 0 && !hasVisibleContent(name.trim());

  // Held back before the request when the web address is under 2 characters (crossmarc/steward-app#2050).
  const slugShort = slugTooShort(effectiveSlug, name.trim().length > 0 || slugTouched);
  // The message waits for the name to be left, the field to be edited, or a submit, so it does not
  // flash at the first keystroke (the button stays enabled so a click can explain).
  const [nameBlurred, setNameBlurred] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const slugShortShown = slugShort && !invisibleName && (nameBlurred || slugTouched || attempted);

  // A click or Enter on an under-length address sends nothing, shows each short field's note and
  // moves focus to the Web address field. The effect runs after that render, so the note is in place.
  const [focusReq, setFocusReq] = useState<{ id: string } | null>(null);
  useEffect(() => {
    if (focusReq) document.getElementById(focusReq.id)?.focus();
  }, [focusReq]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!tenantId || !token || submitting || atCap || invisibleName) return;
    if (slugShort) { setAttempted(true); setFocusReq({ id: 'new-ws-slug' }); return; }
    setSubmitting(true);
    setErr(null);
    try {
      const created = await createApiClient(token).post<CreateWorkspaceResponse>('/workspaces', {
        tenantId, name: name.trim(), slug: effectiveSlug,
      });
      await refresh();
      switchWorkspace(created.workspaceId);
      onClose();
    } catch (e2) {
      // Default-deny (crossmarc/steward-app#2047), keyed on what `POST /workspaces` can raise:
      // appcore's 409 `QUOTA_EXCEEDED` (its text names the plan and a figure, so it is not shown),
      // a 409 for a slug collision (`TRANSACTION_CANCELLED` or any other 409 code), and a façade
      // 400 for a name it refuses, which gets the fallback. The server's `message` is not rendered.
      const msg = curatedErrorMessage(e2, NEW_WORKSPACE_ERROR_COPY, NEW_WORKSPACE_FAILED_MESSAGE, 'Add workspace');
      setErr(msg);
      setSubmitting(false);
    }
  }

  const trapRef = useFocusTrap<HTMLDivElement>(true);

  // Portalled to <body> so the overlay escapes the sidebar's stacking context
  // (the switcher mounts this inside a `position: sticky` <aside>, which traps a
  // plain z-index — the main column's content would otherwise paint over it).
  return createPortal((
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" role="dialog" aria-modal="true" aria-label="Add a workspace" ref={trapRef}>
      <div className="absolute inset-0 bg-ink/40" onClick={onClose} aria-hidden />
      <form
        onSubmit={submit}
        className="relative w-full max-w-[380px] rounded-token border border-line bg-surface p-6 shadow-[0_20px_50px_-12px_rgba(15,23,42,0.3)]"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 flex h-8 w-8 cursor-pointer items-center justify-center rounded-token border-none bg-transparent text-slate transition-colors duration-150 hover:text-navy"
        >
          <X size={16} strokeWidth={ICON_STROKE} aria-hidden />
        </button>

        <div className="font-display text-[16px] font-semibold text-navy mb-1">Add a workspace</div>
        <div className="text-[12px] text-slate font-sans mb-4">Create another workspace in your organization.</div>

        {/* Remaining-quota line / at-cap upsell (WC2). `limit === null` ⇒ unlimited ⇒ no
            line. In this branch limit is a number, so remaining is too (never null). */}
        {quota && quota.limit !== null && (
          atCap ? (
            <div className="mb-4 rounded-token bg-bronze/[0.08] border border-bronze/30 px-3 py-2.5 text-[12px] text-navy font-sans">
              You’ve reached your plan’s limit of {quota.limit} workspace{quota.limit === 1 ? '' : 's'}. To add another, upgrade your plan.
            </div>
          ) : (
            <div className="mb-4 text-[12px] text-mid font-sans">
              {quota.used} of {quota.limit} workspace{quota.limit === 1 ? '' : 's'} used ({quota.remaining} remaining on your plan).
            </div>
          )
        )}

        {!tenantId && (
          <div className="mb-3 rounded-token bg-danger/8 px-3 py-2 text-[12px] text-danger font-sans">
            You don’t belong to an organization yet. Ask an administrator to add you.
          </div>
        )}

        {!atCap && (
          <>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate font-sans mb-1" htmlFor="new-ws-name">Name</label>
            <input
              id="new-ws-name"
              className={`${formCls.input} mb-3`}
              value={name}
              onChange={e => setName(e.target.value)}
              onBlur={() => setNameBlurred(true)}
              maxLength={200}
              placeholder="e.g. Anderson Family"
              autoFocus
              required
            />

            <label className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate font-sans mb-1" htmlFor="new-ws-slug">Web address</label>
            <input
              id="new-ws-slug"
              className={`${formCls.input} mb-1`}
              value={effectiveSlug}
              onChange={e => { setSlugTouched(true); setSlug(e.target.value); }}
              placeholder="anderson-family"
              // Escaped hyphen: modern browsers compile the HTML pattern with the `v`
              // flag, where an unescaped trailing '-' in a class is a syntax error.
              pattern={'[a-z0-9\\-]+'}
              maxLength={SLUG_MAX}
              aria-invalid={slugShortShown || undefined}
              aria-describedby={slugShortShown ? 'new-ws-slug-short' : undefined}
            />
            {slugShortShown && <SlugShortNote id="new-ws-slug-short" />}
            <div className="text-[11px] text-mid font-sans mb-4">Created from the name: lowercase letters, numbers, and hyphens.</div>
          </>
        )}

        {(invisibleName || err) && <div className="mb-3 rounded-token bg-danger/8 px-3 py-2 text-[12px] text-danger font-sans">{invisibleName ? INVISIBLE_NAME_MESSAGE : err}</div>}

        {atCap ? (
          <button
            type="button"
            onClick={onClose}
            className="w-full px-5 py-2.5 text-[11px] font-bold rounded-token border border-line-strong cursor-pointer bg-transparent text-navy uppercase tracking-[0.1em] font-sans"
          >
            Close
          </button>
        ) : (
          <button
            type="submit"
            disabled={!tenantId || !name.trim() || invisibleName || submitting}
            className="w-full px-5 py-2.5 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Adding…' : 'Add workspace'}
          </button>
        )}
      </form>
    </div>
  ), document.body);
}
